import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { Row, Col, Card } from 'react-bootstrap';
import { FaUserFriends, FaUserSecret, FaDesktop, FaHdd } from 'react-icons/fa';

import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Benchmark from '../../components/Benchmark';
import {
  useGetContributorsQuery,
  useGetContributorSystemsQuery,
  useGetContributorSystemDrivesQuery,
  useGetContributorSystemBenchmarksQuery,
} from '../../slices/adminApiSlice';

export default function ContributorsScreen() {
  const { userInfo } = useSelector((state) => state.auth);

  const [expandedContributorId, setExpandedContributorId] = useState(null);
  const [expandedSystemKey, setExpandedSystemKey] = useState(null);
  const [expandedDriveModel, setExpandedDriveModel] = useState(null);

  const { data, isLoading, error } = useGetContributorsQuery(
    userInfo ? {} : skipToken
  );

  const {
    data: systemsData,
    isLoading: isLoadingSystems,
    error: systemsError,
  } = useGetContributorSystemsQuery(
    userInfo && expandedContributorId
      ? { contributorId: expandedContributorId }
      : skipToken
  );

  const {
    data: drivesData,
    isLoading: isLoadingDrives,
    error: drivesError,
  } = useGetContributorSystemDrivesQuery(
    userInfo && expandedContributorId && expandedSystemKey
      ? { contributorId: expandedContributorId, systemKey: expandedSystemKey }
      : skipToken
  );

  const {
    data: benchmarksData,
    isLoading: isLoadingBenchmarks,
    error: benchmarksError,
  } = useGetContributorSystemBenchmarksQuery(
    userInfo && expandedContributorId && expandedSystemKey && expandedDriveModel
      ? {
          contributorId: expandedContributorId,
          systemKey: expandedSystemKey,
          driveModel: expandedDriveModel,
        }
      : skipToken
  );

  if (!userInfo) return <Loader />;

  const renderError = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.data?.message) return err.data.message;
    if (err.error) return err.error;
    try { return JSON.stringify(err); } catch { return String(err); }
  };

  const toggleContributor = (contributorId) => {
    setExpandedSystemKey(null);
    setExpandedDriveModel(null);
    setExpandedContributorId((current) => (current === contributorId ? null : contributorId));
  };

  const toggleSystem = (systemKey) => {
    setExpandedDriveModel(null);
    setExpandedSystemKey((current) => (current === systemKey ? null : systemKey));
  };

  const toggleDrive = (driveModel) => {
    setExpandedDriveModel((current) => (current === driveModel ? null : driveModel));
  };

  const contributorLabel = (c) => {
    if (c.isAnonymous) return 'Anonymous';
    if (c.firstName || c.lastName) return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return 'Unknown User';
  };

  return (
    <>
      <Link
        to={`/admin/home`}
        className='btn btn-light'
        style={{ marginTop: '32px', marginBottom: '16px' }}
      >
        Return to Admin Home
      </Link>

      <h1 style={{ marginBottom: '24px' }}>Contributors</h1>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{renderError(error)}</Message>
      ) : !data?.contributors?.length ? (
        <Message variant='info'>No contributors found.</Message>
      ) : (
        <Row className='g-3 mb-4'>
          {data.contributors.map((c) => (
            <Col key={c.contributorId} md={6} lg={4} xl={3}>
              <Card
                className='shadow-sm h-100'
                role='button'
                tabIndex={0}
                style={{
                  cursor: 'pointer',
                  borderColor: expandedContributorId === c.contributorId ? '#0d6efd' : undefined,
                  borderWidth: expandedContributorId === c.contributorId ? '2px' : undefined,
                }}
                onClick={() => toggleContributor(c.contributorId)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleContributor(c.contributorId); }}
              >
                <Card.Body className='text-center p-4'>
                  {c.isAnonymous ? (
                    <FaUserSecret size={40} className='text-primary mb-3' />
                  ) : (
                    <FaUserFriends size={40} className='text-primary mb-3' />
                  )}
                  <Card.Title className='mb-1'>{contributorLabel(c)}</Card.Title>
                  {!c.isAnonymous && c.primaryEmail && (
                    <div className='small text-muted mb-2'>{c.primaryEmail}</div>
                  )}
                  <div className='small text-muted'>
                    {c.systemCount} system{c.systemCount === 1 ? '' : 's'} &middot;{' '}
                    {c.driveCount} drive{c.driveCount === 1 ? '' : 's'} &middot;{' '}
                    {c.benchmarkCount} benchmark{c.benchmarkCount === 1 ? '' : 's'}
                  </div>
                  <div className='small text-muted'>
                    {c.lastBenchmark
                      ? `Last: ${new Date(c.lastBenchmark).toLocaleDateString()}`
                      : 'No benchmarks yet'}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {expandedContributorId && (
        <div className='mb-4 p-3 border rounded'>
          <h4 className='mb-3'>Systems</h4>
          {isLoadingSystems ? (
            <Loader />
          ) : systemsError ? (
            <Message variant='danger'>{renderError(systemsError)}</Message>
          ) : !systemsData?.systems?.length ? (
            <Message variant='info'>No systems found for this contributor.</Message>
          ) : (
            <Row className='g-3'>
              {systemsData.systems.map((s) => (
                <Col key={s.systemKey} md={6} lg={4}>
                  <Card
                    className='shadow-sm h-100'
                    role='button'
                    tabIndex={0}
                    style={{
                      cursor: 'pointer',
                      borderColor: expandedSystemKey === s.systemKey ? '#0d6efd' : undefined,
                      borderWidth: expandedSystemKey === s.systemKey ? '2px' : undefined,
                    }}
                    onClick={() => toggleSystem(s.systemKey)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSystem(s.systemKey); }}
                  >
                    <Card.Body className='p-3'>
                      <div className='d-flex align-items-center mb-2'>
                        <FaDesktop className='text-primary me-2' />
                        <code
                          title={s.systemId || 'No System ID'}
                          style={{ fontSize: '0.85rem' }}
                        >
                          {s.systemId ? `${s.systemId.substring(0, 8)}…` : 'No System ID'}
                        </code>
                      </div>
                      <div className='small text-muted'>{s.os || '—'} &middot; {s.processorName || '—'}</div>
                      <hr className='my-2' />
                      <div className='small text-muted'>
                        {s.driveModels?.length > 0 ? s.driveModels.join(', ') : '—'}
                      </div>
                      <hr className='my-2' />
                      <div className='small text-muted'>
                        {s.benchmarkCount} benchmark{s.benchmarkCount === 1 ? '' : 's'} &middot;{' '}
                        {s.lastBenchmark ? new Date(s.lastBenchmark).toLocaleDateString() : '—'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}

      {expandedContributorId && expandedSystemKey && (
        <div className='mb-4 p-3 border rounded'>
          <h4 className='mb-3'>Drives</h4>
          {isLoadingDrives ? (
            <Loader />
          ) : drivesError ? (
            <Message variant='danger'>{renderError(drivesError)}</Message>
          ) : !drivesData?.drives?.length ? (
            <Message variant='info'>No drives found for this system.</Message>
          ) : (
            <Row className='g-3'>
              {drivesData.drives.map((d) => (
                <Col key={d.driveModel} md={6} lg={4}>
                  <Card
                    className='shadow-sm h-100'
                    role='button'
                    tabIndex={0}
                    style={{
                      cursor: 'pointer',
                      borderColor: expandedDriveModel === d.driveModel ? '#0d6efd' : undefined,
                      borderWidth: expandedDriveModel === d.driveModel ? '2px' : undefined,
                    }}
                    onClick={() => toggleDrive(d.driveModel)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleDrive(d.driveModel); }}
                  >
                    <Card.Body className='p-3'>
                      <div className='d-flex align-items-center mb-2'>
                        <FaHdd className='text-primary me-2' />
                        <span className='fw-bold'>{d.driveModel || 'Unknown Drive'}</span>
                      </div>
                      <div className='small text-muted'>
                        {d.totalGb ? `${Number(d.totalGb).toFixed(0)} GB` : '—'}
                      </div>
                      <div className='small text-muted'>
                        {d.benchmarkCount} benchmark{d.benchmarkCount === 1 ? '' : 's'} &middot;{' '}
                        {d.lastBenchmark ? new Date(d.lastBenchmark).toLocaleDateString() : '—'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}

      {expandedContributorId && expandedSystemKey && expandedDriveModel && (
        <div className='mb-4 p-3 border rounded'>
          <h4 className='mb-3'>Benchmarks</h4>
          {isLoadingBenchmarks ? (
            <Loader />
          ) : benchmarksError ? (
            <Message variant='danger'>{renderError(benchmarksError)}</Message>
          ) : !benchmarksData?.benchmarks?.length ? (
            <Message variant='info'>No benchmarks found for this drive.</Message>
          ) : (
            benchmarksData.benchmarks.map((b) => (
              <Benchmark key={b._id} benchmark={b} />
            ))
          )}
        </div>
      )}
    </>
  );
}
